import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ScubaDiving, Balance } from '@mui/icons-material';
import { AppOptions, OptionsManager, getTankDisplayName } from '../utils/optionsManager';

type ExposureSuit = 'none' | 'rashguard' | 'shorty' | '3mm' | '5mm' | '7mm' | 'drysuit';
type WaterType = 'fresh' | 'salt';

const SUIT_BUOYANCY_KG: Record<ExposureSuit, number> = {
  none: 0,
  rashguard: 0.25,
  shorty: 0.8,
  '3mm': 1.5,
  '5mm': 2.5,
  '7mm': 4,
  drysuit: 5.5,
};

const TANK_ADJUSTMENT_KG: Record<AppOptions['equipment']['tankSize'], number> = {
  AL80: 1,
  AL63: 0.7,
  AL100: 0.3,
  Steel72: -0.5,
  Steel100: -1.2,
  Steel120: -2,
  Steel130: -2.4,
  custom: 0,
};

const roundToNearestHalf = (value: number) => Math.round(value * 2) / 2;
const poundsToKilograms = (value: number) => value * 0.45359237;
const kilogramsToPounds = (value: number) => value / 0.45359237;

const formatWeight = (valueKg: number, unit: AppOptions['units']['weight']) => {
  if (unit === 'lbs') {
    return `${Math.round(kilogramsToPounds(valueKg))} lb`;
  }

  return `${roundToNearestHalf(valueKg).toFixed(1)} kg`;
};

const parseDisplayWeight = (value: string, unit: AppOptions['units']['weight']) => {
  const parsed = parseFloat(value);
  if (Number.isNaN(parsed) || parsed < 0) {
    return 0;
  }

  return unit === 'lbs' ? poundsToKilograms(parsed) : parsed;
};

const BuoyancyCalculator: React.FC = () => {
  const optionsManager = OptionsManager.getInstance();
  const initialOptions = optionsManager.getOptions();

  const [weightUnit, setWeightUnit] = useState<AppOptions['units']['weight']>(initialOptions.units.weight);
  const [defaultTankSize, setDefaultTankSize] = useState<AppOptions['equipment']['tankSize']>(initialOptions.equipment.tankSize);
  const [diverWeightKg, setDiverWeightKg] = useState(79.5);
  const [waterType, setWaterType] = useState<WaterType>('salt');
  const [exposureSuit, setExposureSuit] = useState<ExposureSuit>('5mm');
  const [tankSize, setTankSize] = useState<AppOptions['equipment']['tankSize']>(initialOptions.equipment.tankSize);
  const [extraGearKg, setExtraGearKg] = useState(0);

  useEffect(() => {
    const unsubscribe = optionsManager.subscribe((options) => {
      setWeightUnit(options.units.weight);
      setDefaultTankSize(options.equipment.tankSize);
      setTankSize((currentTankSize) => currentTankSize === defaultTankSize ? options.equipment.tankSize : currentTankSize);
    });

    return unsubscribe;
  }, [defaultTankSize, optionsManager]);

  const bodyWeightContributionKg = diverWeightKg * (waterType === 'salt' ? 0.04 : 0.02);
  const suitContributionKg = SUIT_BUOYANCY_KG[exposureSuit];
  const tankContributionKg = TANK_ADJUSTMENT_KG[tankSize];
  const estimatedWeightKg = Math.max(0, roundToNearestHalf(bodyWeightContributionKg + suitContributionKg + tankContributionKg + extraGearKg));
  const lowerRangeKg = Math.max(0, roundToNearestHalf(estimatedWeightKg - 0.5));
  const upperRangeKg = roundToNearestHalf(estimatedWeightKg + 0.5);

  return (
    <Card elevation={2} sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Balance color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Buoyancy Calculator
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Estimate a starting ballast point for your next buoyancy check using your body weight, water type, suit, cylinder choice, and extra gear.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={`Diver Weight (${weightUnit})`}
              type="number"
              value={weightUnit === 'lbs' ? Math.round(kilogramsToPounds(diverWeightKg)) : diverWeightKg}
              onChange={(event) => setDiverWeightKg(parseDisplayWeight(event.target.value, weightUnit))}
              inputProps={{ min: 0, step: weightUnit === 'lbs' ? 1 : 0.5 }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="buoyancy-water-type-label">Water Type</InputLabel>
              <Select
                labelId="buoyancy-water-type-label"
                value={waterType}
                label="Water Type"
                onChange={(event) => setWaterType(event.target.value as WaterType)}
              >
                <MenuItem value="fresh">Fresh Water</MenuItem>
                <MenuItem value="salt">Salt Water</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="buoyancy-suit-label">Exposure Suit</InputLabel>
              <Select
                labelId="buoyancy-suit-label"
                value={exposureSuit}
                label="Exposure Suit"
                onChange={(event) => setExposureSuit(event.target.value as ExposureSuit)}
              >
                <MenuItem value="none">No Suit / Skin</MenuItem>
                <MenuItem value="rashguard">Rashguard / Dive Skin</MenuItem>
                <MenuItem value="shorty">Shorty</MenuItem>
                <MenuItem value="3mm">3mm Full Suit</MenuItem>
                <MenuItem value="5mm">5mm Full Suit</MenuItem>
                <MenuItem value="7mm">7mm Full Suit</MenuItem>
                <MenuItem value="drysuit">Drysuit</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="buoyancy-tank-label">Cylinder</InputLabel>
              <Select
                labelId="buoyancy-tank-label"
                value={tankSize}
                label="Cylinder"
                onChange={(event) => setTankSize(event.target.value as AppOptions['equipment']['tankSize'])}
              >
                <MenuItem value={defaultTankSize}>Default: {getTankDisplayName(defaultTankSize)}</MenuItem>
                <MenuItem value="AL80">AL80 (11.0L)</MenuItem>
                <MenuItem value="AL63">AL63 (8.5L)</MenuItem>
                <MenuItem value="AL100">AL100 (13.2L)</MenuItem>
                <MenuItem value="Steel72">Steel 72 (9.6L)</MenuItem>
                <MenuItem value="Steel100">Steel 100 (12.0L)</MenuItem>
                <MenuItem value="Steel120">Steel 120 (15.1L)</MenuItem>
                <MenuItem value="Steel130">Steel 130 (16.4L)</MenuItem>
                <MenuItem value="custom">Custom / Unknown Cylinder</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={`Extra Gear / Camera / Stages (${weightUnit})`}
              type="number"
              value={weightUnit === 'lbs' ? Math.round(kilogramsToPounds(extraGearKg)) : extraGearKg}
              onChange={(event) => setExtraGearKg(parseDisplayWeight(event.target.value, weightUnit))}
              inputProps={{ min: 0, step: weightUnit === 'lbs' ? 1 : 0.5 }}
              helperText="Use this for lights, camera rigs, or anything that changes your ballast needs."
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 3,
            p: 2.5,
            borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(0, 172, 193, 0.10) 0%, rgba(33, 150, 243, 0.08) 100%)',
            border: '1px solid rgba(0, 172, 193, 0.18)',
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <ScubaDiving color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Estimated Starting Ballast
            </Typography>
          </Box>

          <Typography variant="h3" color="primary" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
            {formatWeight(estimatedWeightKg, weightUnit)}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
            Practical range: {formatWeight(lowerRangeKg, weightUnit)} to {formatWeight(upperRangeKg, weightUnit)}
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
            <Chip label={`Body: ${formatWeight(bodyWeightContributionKg, weightUnit)}`} size="small" />
            <Chip label={`Suit: ${formatWeight(suitContributionKg, weightUnit)}`} size="small" />
            <Chip label={`Tank: ${tankContributionKg >= 0 ? '+' : ''}${formatWeight(tankContributionKg, weightUnit)}`} size="small" />
            <Chip label={`Extras: ${formatWeight(extraGearKg, weightUnit)}`} size="small" />
          </Stack>
        </Box>

        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            This is a starting estimate, not a final answer. Verify it with a normal end-of-dive buoyancy check: near-empty cylinder, 3-5 m / 10-15 ft stop depth, empty BCD, and normal breathing.
          </Typography>
        </Alert>
      </CardContent>
    </Card>
  );
};

export default BuoyancyCalculator;